import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RePieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import {
  Phone, Search, Plus, Upload, Download, ChevronLeft, ChevronRight,
  Clock, Bell, MessageCircle, User, FileText, Calendar, X,
  PhoneCall, AlertCircle, CheckCircle, ArrowLeft, Edit2, Save,
  LayoutDashboard, List, UserCheck, BarChart3, PlusCircle, TrendingUp,
  Filter, RefreshCw, Target, Award, PhoneForwarded, XCircle, Eye, Trash2, Settings
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_STATUS_COLORS = {
  "ARANMAYAN DATA": "bg-cyan-500 text-white",
  "CEVAP YOK": "bg-blue-600 text-white",
  "DÜŞÜNECEK": "bg-green-500 text-white",
  "KAPALI": "bg-blue-800 text-white",
  "KARAR ARAMASI CY": "bg-blue-500 text-white",
  "KAYIT AŞAMASI-AYNI GÜN": "bg-green-600 text-white",
  "KAYIT AŞAMASI-İLERİ TARİH": "bg-green-700 text-white",
  "KENDİSİ ARAYACAK OLUMLU": "bg-emerald-500 text-white",
  "KENDİSİ ARAYACAK OLUMSUZ": "bg-orange-500 text-white",
  "KURUMA GELECEK": "bg-teal-500 text-white",
  "MEŞGUL": "bg-gray-500 text-white",
  "MÜSAİT DEĞİL": "bg-gray-600 text-white",
  "BAŞKA DANIŞMANDA": "bg-purple-500 text-white",
  "POTANSİYEL": "bg-indigo-500 text-white",
  "KAYIT OK": "bg-emerald-700 text-white",
  "RANDEVU": "bg-pink-500 text-white",
  "FİYAT FAZLA": "bg-amber-600 text-white",
  "GENEL GÖRÜŞME": "bg-sky-500 text-white",
  "NO SERVİS DIŞI": "bg-red-500 text-white",
  "NUMARAMI ENGELLEDİ": "bg-red-700 text-white",
  "Fake Başvuru": "bg-red-400 text-white",
  "MUHASEBE": "bg-violet-500 text-white",
  "BAŞKASINA ATA": "bg-yellow-600 text-white",
  "SIKINTILI": "bg-rose-600 text-white",
};

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316', '#14b8a6', '#6366f1'];

const FILTER_STATUSES = [
  "POTANSİYEL", "ARANMAYAN DATA", "CEVAP YOK", "DÜŞÜNECEK", "KAPALI",
  "KARAR ARAMASI CY", "KAYIT AŞAMASI-AYNI GÜN", "KAYIT AŞAMASI-İLERİ TARİH",
  "KENDİSİ ARAYACAK OLUMLU", "KURUMA GELECEK", "MEŞGUL", "MÜSAİT DEĞİL"
];

const CRM_TABS = [
  { id: 'overview', label: 'Genel Bakis', icon: LayoutDashboard },
  { id: 'data-list', label: 'Data Listesi', icon: List },
  { id: 'new-entry', label: 'Yeni Data Girisi', icon: PlusCircle },
  { id: 'reports', label: 'Raporlar', icon: BarChart3 },
  { id: 'agent-view', label: 'Danısman Gorunumu', icon: UserCheck },
  { id: 'status-settings', label: 'Durum Yonetimi', icon: Settings, adminOnly: true },
];

export default function CRMDataTracker({ token, isAdmin = true }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [agents, setAgents] = useState([]);
  const [reports, setReports] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [newDataCount, setNewDataCount] = useState(0);
  const prevCountRef = useRef(0);
  const [statusColors, setStatusColors] = useState(DEFAULT_STATUS_COLORS);
  const [statusList, setStatusList] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch statuses from backend
  const fetchStatuses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/crm/statuses`, { headers });
      const statuses = res.data.statuses || [];
      const colorMap = {};
      statuses.forEach(s => {
        colorMap[s.name] = `${s.color} text-white`;
      });
      setStatusColors(prev => ({ ...prev, ...colorMap }));
      setStatusList(statuses);
    } catch (err) { /* use defaults */ }
  }, [token]);

  // Notification sound
  const playNotificationSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Double tick sound
      [0, 0.15].forEach(delay => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.3, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.1);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.1);
      });
    } catch (e) { console.log('Sound error:', e); }
  }, []);

  // Poll for new data
  useEffect(() => {
    const checkNewData = async () => {
      try {
        const res = await axios.get(`${API}/api/crm/new-data-count`, { headers });
        const count = res.data.new_today || 0;
        if (count > prevCountRef.current && prevCountRef.current > 0) {
          playNotificationSound();
        }
        prevCountRef.current = count;
        setNewDataCount(count);
      } catch (err) { /* silent */ }
    };
    checkNewData();
    const interval = setInterval(checkNewData, 30000);
    return () => clearInterval(interval);
  }, [token, playNotificationSound]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/crm/stats`, { headers });
      setStats(res.data);
    } catch (err) { console.error(err); }
  }, [token]);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/crm/agents-list`, { headers });
      setAgents(res.data);
    } catch (err) { console.error(err); }
  }, [token]);

  const fetchReports = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/crm/reports`, { headers });
      setReports(res.data);
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => { fetchStats(); fetchAgents(); fetchStatuses(); }, [fetchStats, fetchAgents, fetchStatuses]);
  useEffect(() => {
    if (activeTab === 'overview' || activeTab === 'reports' || activeTab === 'agent-view') {
      fetchReports();
    }
  }, [activeTab, fetchReports]);

  if (selectedLead) {
    return <LeadDetail
      lead={selectedLead}
      token={token}
      agents={agents}
      onBack={() => { setSelectedLead(null); fetchStats(); }}
      statusColors={statusColors}
      statusList={statusList}
    />;
  }

  return (
    <div className="space-y-0" data-testid="crm-data-tracker">
      {/* CRM Header */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 rounded-xl p-5 mb-5 shadow-lg" data-testid="crm-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Data CRM</h1>
            <p className="text-blue-200 text-sm mt-1">
              {stats.total || 0} toplam data &middot; Bugun {stats.today_actions || 0} islem &middot; {stats.reminders_count || 0} hatirlatma
            </p>
          </div>
          <div className="flex items-center gap-3">
            {newDataCount > 0 && (
              <div className="flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/30 rounded-lg px-3 py-1.5 animate-pulse" data-testid="new-data-badge">
                <Bell size={15} className="text-emerald-300" />
                <span className="text-emerald-200 text-sm font-semibold">{newDataCount} yeni data</span>
              </div>
            )}
            <button onClick={() => { fetchStats(); fetchReports(); }} className="p-2 hover:bg-white/10 rounded-lg transition" data-testid="refresh-btn">
              <RefreshCw size={18} className="text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Sub-Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl mb-5 overflow-x-auto" data-testid="crm-tabs">
        {CRM_TABS.filter(tab => !tab.adminOnly || isAdmin).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
            }`}
            data-testid={`crm-tab-${tab.id}`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <CRMOverview stats={stats} reports={reports} onNavigate={setActiveTab} />
      )}
      {activeTab === 'data-list' && (
        <CRMDataList token={token} agents={agents} isAdmin={isAdmin} onSelectLead={setSelectedLead} statusColors={statusColors} statusList={statusList} />
      )}
      {activeTab === 'new-entry' && (
        <CRMNewEntry token={token} agents={agents} onSaved={() => { setActiveTab('data-list'); fetchStats(); }} />
      )}
      {activeTab === 'reports' && (
        <CRMReports reports={reports} />
      )}
      {activeTab === 'agent-view' && (
        <CRMAgentView reports={reports} token={token} agents={agents} onSelectLead={setSelectedLead} statusColors={statusColors} />
      )}
      {activeTab === 'status-settings' && isAdmin && (
        <StatusSettings token={token} statusList={statusList} onRefresh={fetchStatuses} />
      )}
    </div>
  );
}

// ==================== OVERVIEW TAB ====================
function CRMOverview({ stats, reports, onNavigate }) {
  const statusData = reports?.status_distribution?.slice(0, 8) || [];
  const dailyTrend = reports?.daily_trend || [];
  const topAgents = reports?.agent_performance?.slice(0, 5) || [];

  const statCards = [
    { label: 'Toplam Data', value: stats.total || 0, icon: FileText, color: 'from-blue-500 to-blue-600', text: 'text-blue-600' },
    { label: 'Bugunki Islemler', value: stats.today_actions || 0, icon: PhoneCall, color: 'from-emerald-500 to-emerald-600', text: 'text-emerald-600' },
    { label: 'Aktif Hatirlatma', value: stats.reminders_count || 0, icon: Bell, color: 'from-amber-500 to-amber-600', text: 'text-amber-600' },
    { label: 'Kayit OK', value: stats.status_counts?.['KAYIT OK'] || 0, icon: CheckCircle, color: 'from-green-500 to-green-600', text: 'text-green-600' },
    { label: 'Potansiyel', value: stats.status_counts?.['POTANSİYEL'] || 0, icon: Target, color: 'from-indigo-500 to-indigo-600', text: 'text-indigo-600' },
    { label: 'Aranmayan', value: stats.status_counts?.['ARANMAYAN DATA'] || 0, icon: AlertCircle, color: 'from-red-500 to-red-600', text: 'text-red-600' },
  ];

  return (
    <div className="space-y-5" data-testid="crm-overview">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {statCards.map((card, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid={`stat-card-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${card.color} flex items-center justify-center`}>
                <card.icon size={16} className="text-white" />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-800">{card.value.toLocaleString('tr-TR')}</div>
            <div className="text-xs text-slate-500 mt-0.5">{card.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Daily Trend Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <TrendingUp size={16} className="text-blue-500" /> Son 7 Gun - Islem Trendi
          </h3>
          {dailyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={dailyTrend}>
                <defs>
                  <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
                <Area type="monotone" dataKey="actions" name="Islem" stroke="#3b82f6" fill="url(#areaGrad)" strokeWidth={2} />
                <Area type="monotone" dataKey="new_kayit" name="Kayit OK" stroke="#10b981" fill="none" strokeWidth={2} strokeDasharray="4 4" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">Veri bekleniyor...</p>}
        </div>

        {/* Status Distribution Pie */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <BarChart3 size={16} className="text-indigo-500" /> Durum Dagilimi
          </h3>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <RePieChart>
                <Pie data={statusData} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} innerRadius={40}>
                  {statusData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 11 }} />
              </RePieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">Veri bekleniyor...</p>}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {statusData.slice(0, 5).map((s, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-[10px] text-slate-500">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                {s.status} ({s.count})
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Top Agents + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top Agents */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Award size={16} className="text-amber-500" /> En Iyi Danismanlar
          </h3>
          <div className="space-y-2">
            {topAgents.map((agent, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                  i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-slate-400' : i === 2 ? 'bg-amber-700' : 'bg-slate-300'
                }`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{agent.name}</div>
                  <div className="text-xs text-slate-400">{agent.total} data &middot; %{agent.conversion_rate} donusum</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-emerald-600">{agent.kayit_ok}</div>
                  <div className="text-[10px] text-slate-400">Kayit OK</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Hizli Islemler</h3>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => onNavigate('new-entry')} className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-dashed border-blue-200 hover:border-blue-400 hover:bg-blue-50/50 transition group" data-testid="quick-new-entry">
              <PlusCircle size={24} className="text-blue-400 group-hover:text-blue-600 transition" />
              <span className="text-xs font-medium text-slate-600">Yeni Data Girisi</span>
            </button>
            <button onClick={() => onNavigate('data-list')} className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-dashed border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50/50 transition group" data-testid="quick-data-list">
              <List size={24} className="text-emerald-400 group-hover:text-emerald-600 transition" />
              <span className="text-xs font-medium text-slate-600">Data Listesi</span>
            </button>
            <button onClick={() => onNavigate('reports')} className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-dashed border-violet-200 hover:border-violet-400 hover:bg-violet-50/50 transition group" data-testid="quick-reports">
              <BarChart3 size={24} className="text-violet-400 group-hover:text-violet-600 transition" />
              <span className="text-xs font-medium text-slate-600">Raporlar</span>
            </button>
            <button onClick={() => onNavigate('agent-view')} className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-dashed border-amber-200 hover:border-amber-400 hover:bg-amber-50/50 transition group" data-testid="quick-agent-view">
              <UserCheck size={24} className="text-amber-400 group-hover:text-amber-600 transition" />
              <span className="text-xs font-medium text-slate-600">Danısman Gorunumu</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== DATA LIST TAB ====================
function CRMDataList({ token, agents, isAdmin, onSelectLead, statusColors, statusList }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [listTab, setListTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimeout = useRef(null);
  const [migrating, setMigrating] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAgent, setBulkAgent] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, per_page: perPage, tab: listTab };
      if (statusFilter) params.status = statusFilter;
      if (agentFilter) params.agent_name = agentFilter;
      if (searchQuery) params.search = searchQuery;
      const res = await axios.get(`${API}/api/crm/leads`, { headers, params });
      setLeads(res.data.leads);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('CRM leads fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, listTab, statusFilter, agentFilter, searchQuery, token]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleSearch = (val) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => { setSearchQuery(val); setPage(1); }, 500);
  };

  const handleMigrate = async () => {
    if (!window.confirm('KariyerPaneli verilerini CRM sistemine aktarmak istediginize emin misiniz?')) return;
    setMigrating(true);
    try {
      const res = await axios.post(`${API}/api/crm/migrate-from-kariyer`, {}, { headers });
      alert(`Aktarim tamamlandi: ${res.data.migrated} yeni, ${res.data.updated} guncellenen`);
      fetchLeads();
    } catch (err) {
      alert('Aktarim hatasi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setMigrating(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === leads.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(leads.map(l => l.id)));
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAgent || selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size} datayi ${bulkAgent} danismanina aktarmak istediginize emin misiniz?`)) return;
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/api/crm/bulk-assign`, {
        lead_ids: Array.from(selectedIds),
        agent_name: bulkAgent
      }, { headers });
      alert(res.data.message);
      setSelectedIds(new Set());
      setBulkAgent('');
      fetchLeads();
    } catch (err) {
      alert('Atama hatasi: ' + (err.response?.data?.detail || err.message));
    } finally { setBulkLoading(false); }
  };

  const handleBulkStatus = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size} datanin durumunu ${bulkStatus} olarak degistirmek istediginize emin misiniz?`)) return;
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/api/crm/bulk-status`, {
        lead_ids: Array.from(selectedIds),
        status: bulkStatus
      }, { headers });
      alert(res.data.message);
      setSelectedIds(new Set());
      setBulkStatus('');
      fetchLeads();
    } catch (err) {
      alert('Durum guncelleme hatasi: ' + (err.response?.data?.detail || err.message));
    } finally { setBulkLoading(false); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size} datayi kalici olarak silmek istediginize emin misiniz? Bu islem geri alinamaz!`)) return;
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/api/crm/bulk-delete`, {
        lead_ids: Array.from(selectedIds)
      }, { headers });
      alert(res.data.message);
      setSelectedIds(new Set());
      fetchLeads();
    } catch (err) {
      alert('Silme hatasi: ' + (err.response?.data?.detail || err.message));
    } finally { setBulkLoading(false); }
  };

  const listTabs = [
    { id: 'all', label: 'Tum Datalar', icon: FileText },
    { id: 'stale', label: 'Eskiyen Potansiyeller', icon: AlertCircle },
    { id: 'recent_calls', label: 'Son Aramalar', icon: PhoneCall },
    { id: 'reminders', label: 'Hatirlatmalar', icon: Bell },
  ];

  const allSelected = leads.length > 0 && selectedIds.size === leads.length;
  const someSelected = selectedIds.size > 0;

  return (
    <div className="space-y-4" data-testid="crm-data-list">
      {/* Sub-filter tabs + actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1.5 bg-slate-50 p-1 rounded-lg">
          {listTabs.map(t => (
            <button
              key={t.id}
              onClick={() => { setListTab(t.id); setPage(1); setStatusFilter(''); setSelectedIds(new Set()); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all ${
                listTab === t.id ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:bg-white'
              }`}
              data-testid={`list-tab-${t.id}`}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {isAdmin && <Button size="sm" variant="outline" onClick={() => { window.dispatchEvent(new CustomEvent('crm-show-import')); }} className="text-xs" data-testid="import-btn">
            <Upload size={14} className="mr-1" /> Excel
          </Button>}
          {isAdmin && <Button size="sm" variant="outline" onClick={handleMigrate} disabled={migrating} className="text-xs" data-testid="migrate-btn">
            <Download size={14} className="mr-1" /> {migrating ? 'Aktariliyor...' : 'KariyerPaneli'}
          </Button>}
        </div>
      </div>

      {/* Bulk Action Bar */}
      {isAdmin && someSelected && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-3 flex-wrap animate-in fade-in" data-testid="bulk-action-bar">
          <div className="flex items-center gap-2">
            <CheckCircle size={16} className="text-blue-600" />
            <span className="text-sm font-semibold text-blue-800">{selectedIds.size} data secildi</span>
            <button onClick={() => setSelectedIds(new Set())} className="text-xs text-blue-500 hover:underline ml-1">Temizle</button>
          </div>
          <div className="h-5 w-px bg-blue-200" />
          <div className="flex items-center gap-2 flex-wrap flex-1">
            <Select value={bulkAgent} onValueChange={setBulkAgent}>
              <SelectTrigger className="w-[180px] bg-white border-blue-200 h-8 text-xs" data-testid="bulk-agent-select">
                <SelectValue placeholder="Danismana Aktar..." />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-800">
                {agents.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={handleBulkAssign} disabled={!bulkAgent || bulkLoading} className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs" data-testid="bulk-assign-btn">
              <UserCheck size={13} className="mr-1" /> Aktar
            </Button>
            <div className="h-5 w-px bg-blue-200" />
            <Select value={bulkStatus} onValueChange={setBulkStatus}>
              <SelectTrigger className="w-[170px] bg-white border-blue-200 h-8 text-xs" data-testid="bulk-status-select">
                <SelectValue placeholder="Durum Degistir..." />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-800 max-h-[300px]">
                {statusList.map(s => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={handleBulkStatus} disabled={!bulkStatus || bulkLoading} className="bg-amber-500 hover:bg-amber-600 text-white h-8 text-xs" data-testid="bulk-status-btn">
              <Edit2 size={13} className="mr-1" /> Uygula
            </Button>
            <div className="h-5 w-px bg-blue-200" />
            <Button size="sm" onClick={handleBulkDelete} disabled={bulkLoading} className="bg-red-600 hover:bg-red-700 text-white h-8 text-xs" data-testid="bulk-delete-btn">
              <Trash2 size={13} className="mr-1" /> Sil
            </Button>
          </div>
        </div>
      )}

      {/* Status chips */}
      {listTab === 'all' && (
        <div className="flex flex-wrap gap-1.5" data-testid="status-filters">
          <button
            onClick={() => { setStatusFilter(''); setPage(1); }}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors ${
              !statusFilter ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            TUMU
          </button>
          {FILTER_STATUSES.map(s => (
            <button
              key={s}
              onClick={() => { setStatusFilter(statusFilter === s ? '' : s); setPage(1); setSelectedIds(new Set()); }}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-all ${
                statusFilter === s ? statusColors[s] || 'bg-slate-500 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
              }`}
              data-testid={`filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Search row */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Ad, telefon veya email ile ara..."
            className="pl-9 bg-white border-slate-200 h-9 text-sm"
            onChange={(e) => handleSearch(e.target.value)}
            data-testid="search-input"
          />
        </div>
        {isAdmin && <Select value={agentFilter} onValueChange={(v) => { setAgentFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[180px] bg-white border-slate-200 h-9 text-sm" data-testid="agent-filter">
            <SelectValue placeholder="Danısman" />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200 text-slate-800">
            <SelectItem value="all">Tum Danismanlar</SelectItem>
            {agents.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>}
        <Select value={String(perPage)} onValueChange={(v) => { setPerPage(Number(v)); setPage(1); }}>
          <SelectTrigger className="w-[80px] bg-white border-slate-200 h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200 text-slate-800">
            {[25, 50, 100].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-slate-50/80">
              <tr className="border-b border-slate-200">
                {isAdmin && (
                  <th className="p-3 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                )}
                <th className="p-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-10">#</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Ad Soyad</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Telefon</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Durum</th>
                <th className="p-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Arama</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Danısman</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Ders</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tarih/Saat</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={isAdmin ? 9 : 8} className="p-12 text-center">
                  <div className="inline-flex items-center gap-2 text-slate-400"><RefreshCw size={16} className="animate-spin" /> Yukleniyor...</div>
                </td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={isAdmin ? 9 : 8} className="p-12 text-center text-slate-400">Veri bulunamadi</td></tr>
              ) : leads.map((lead, idx) => {
                const isSelected = selectedIds.has(lead.id);
                return (
                  <tr
                    key={lead.id}
                    className={`cursor-pointer transition-colors group ${isSelected ? 'bg-blue-50' : 'hover:bg-blue-50/40'}`}
                    data-testid={`lead-row-${idx}`}
                  >
                    {isAdmin && (
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(lead.id)}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          data-testid={`checkbox-${idx}`}
                        />
                      </td>
                    )}
                    <td className="p-3" onClick={() => onSelectLead(lead)}>
                      <span className="text-xs text-slate-400 font-mono">{(page - 1) * perPage + idx + 1}</span>
                    </td>
                    <td className="p-3" onClick={() => onSelectLead(lead)}>
                      <span className="font-medium text-slate-800 group-hover:text-blue-700 transition-colors">{lead.customer_name}</span>
                    </td>
                    <td className="p-3 text-slate-500 font-mono text-xs" onClick={() => onSelectLead(lead)}>{lead.phone}</td>
                    <td className="p-3" onClick={() => onSelectLead(lead)}>
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${statusColors[lead.status] || 'bg-slate-200 text-slate-600'}`}>
                        {lead.status}
                      </span>
                    </td>
                    <td className="p-3 text-center" onClick={() => onSelectLead(lead)}>
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${
                        (lead.call_count || 0) > 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'
                      }`}>{lead.call_count || 0}</span>
                    </td>
                    <td className="p-3 text-xs text-slate-600" onClick={() => onSelectLead(lead)}>{lead.agent_name || '-'}</td>
                    <td className="p-3 text-xs text-slate-600 max-w-[120px] truncate" onClick={() => onSelectLead(lead)}>{lead.course_name || '-'}</td>
                    <td className="p-3 text-xs text-slate-500" onClick={() => onSelectLead(lead)}>
                      <div>{lead.data_date || '-'}</div>
                      {lead.record_datetime && lead.record_datetime.length > 10 && (
                        <div className="text-[10px] text-slate-400">{lead.record_datetime.slice(11, 16)}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50">
          <span className="text-xs text-slate-400">
            {someSelected && <span className="text-blue-600 font-medium mr-2">{selectedIds.size} secili</span>}
            {total > 0 ? `${(page - 1) * perPage + 1}-${Math.min(page * perPage, total)} / ${total.toLocaleString('tr-TR')}` : '0 kayit'}
          </span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 w-7 p-0" data-testid="prev-page">
              <ChevronLeft size={14} />
            </Button>
            <span className="px-2 text-xs text-slate-500 font-medium">{page}/{totalPages}</span>
            <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 w-7 p-0" data-testid="next-page">
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== NEW ENTRY TAB ====================
function CRMNewEntry({ token, agents, onSaved }) {
  const [form, setForm] = useState({
    customer_name: '', phone: '', email: '', city: '', education_level: '',
    gender: '', profession: '', course_name: '', ad_type: '', agent_name: '', note: ''
  });
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const handler = () => setShowImport(true);
    window.addEventListener('crm-show-import', handler);
    return () => window.removeEventListener('crm-show-import', handler);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.phone) return alert('Ad ve telefon zorunludur');
    setSaving(true);
    try {
      await axios.post(`${API}/api/crm/leads`, form, { headers });
      setForm({ customer_name: '', phone: '', email: '', city: '', education_level: '', gender: '', profession: '', course_name: '', ad_type: '', agent_name: '', note: '' });
      onSaved();
    } catch (err) {
      alert('Kayit hatasi: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  if (showImport) {
    return <ImportModal token={token} onClose={() => setShowImport(false)} onImported={() => { setShowImport(false); onSaved(); }} />;
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="crm-new-entry">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <PlusCircle size={20} className="text-blue-600" /> Yeni Data Girisi
          </h2>
          <p className="text-sm text-slate-500 mt-1">Yeni musteri adayi bilgilerini girin</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Personal Info Section */}
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Kisisel Bilgiler</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Ad Soyad *" value={form.customer_name} onChange={v => set('customer_name', v)} required />
              <FormField label="Telefon *" value={form.phone} onChange={v => set('phone', v)} required />
              <FormField label="E-posta" value={form.email} onChange={v => set('email', v)} />
              <FormField label="Sehir" value={form.city} onChange={v => set('city', v)} />
              <FormField label="Egitim Durumu" value={form.education_level} onChange={v => set('education_level', v)} />
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">Cinsiyet</label>
                <Select value={form.gender} onValueChange={v => set('gender', v)}>
                  <SelectTrigger className="bg-white border-slate-200 h-10"><SelectValue placeholder="Seciniz" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-800">
                    <SelectItem value="KADIN">Kadin</SelectItem>
                    <SelectItem value="ERKEK">Erkek</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <FormField label="Meslek" value={form.profession} onChange={v => set('profession', v)} />
            </div>
          </div>

          {/* Course Info Section */}
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Ders & Atama</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Ders Adi" value={form.course_name} onChange={v => set('course_name', v)} />
              <FormField label="Reklam Turu" value={form.ad_type} onChange={v => set('ad_type', v)} />
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">Danısman</label>
                <Select value={form.agent_name} onValueChange={v => set('agent_name', v)}>
                  <SelectTrigger className="bg-white border-slate-200 h-10"><SelectValue placeholder="Seciniz" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-800">
                    {agents.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Notlar</h3>
            <textarea
              className="w-full p-3 border border-slate-200 rounded-lg text-sm bg-white resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              rows={3}
              placeholder="Data hakkinda not ekleyin..."
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white flex-1 h-11" data-testid="form-submit">
              {saving ? 'Kaydediliyor...' : 'Data Kaydet'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowImport(true)} className="h-11">
              <Upload size={16} className="mr-2" /> Excel Yukle
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, required = false }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-500 mb-1.5 block">{label}</label>
      <Input
        className="bg-white border-slate-200 h-10"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        required={required}
      />
    </div>
  );
}

// ==================== REPORTS TAB ====================
function CRMReports({ reports }) {
  if (!reports) return <div className="text-center py-12 text-slate-400">Rapor verileri yukleniyor...</div>;

  const agentData = reports.agent_performance || [];
  const statusData = reports.status_distribution || [];
  const courseData = reports.course_distribution || [];
  const adData = reports.ad_distribution || [];
  const dailyTrend = reports.daily_trend || [];

  return (
    <div className="space-y-5" data-testid="crm-reports">
      {/* Agent Performance Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="p-4 border-b border-slate-100 flex items-center gap-2">
          <UserCheck size={16} className="text-blue-500" />
          <h3 className="text-sm font-semibold text-slate-700">Danısman Performans Tablosu</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80">
              <tr className="border-b border-slate-200">
                <th className="p-3 text-left text-xs font-semibold text-slate-500">Danısman</th>
                <th className="p-3 text-center text-xs font-semibold text-slate-500">Toplam</th>
                <th className="p-3 text-center text-xs font-semibold text-emerald-600">Kayit OK</th>
                <th className="p-3 text-center text-xs font-semibold text-indigo-600">Potansiyel</th>
                <th className="p-3 text-center text-xs font-semibold text-cyan-600">Aranmayan</th>
                <th className="p-3 text-center text-xs font-semibold text-blue-600">Cevap Yok</th>
                <th className="p-3 text-center text-xs font-semibold text-green-600">Dusunecek</th>
                <th className="p-3 text-center text-xs font-semibold text-slate-500">Ort. Arama</th>
                <th className="p-3 text-center text-xs font-semibold text-amber-600">Donusum %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {agentData.map((agent, i) => (
                <tr key={i} className="hover:bg-slate-50 transition">
                  <td className="p-3 font-medium text-slate-800">{agent.name}</td>
                  <td className="p-3 text-center font-bold text-slate-600">{agent.total}</td>
                  <td className="p-3 text-center"><span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-bold">{agent.kayit_ok}</span></td>
                  <td className="p-3 text-center"><span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-bold">{agent.potansiyel}</span></td>
                  <td className="p-3 text-center text-slate-500">{agent.aranmayan}</td>
                  <td className="p-3 text-center text-slate-500">{agent.cevap_yok}</td>
                  <td className="p-3 text-center text-slate-500">{agent.dusunecek}</td>
                  <td className="p-3 text-center text-slate-500">{agent.avg_calls}</td>
                  <td className="p-3 text-center">
                    <span className={`font-bold text-xs ${agent.conversion_rate >= 5 ? 'text-emerald-600' : agent.conversion_rate >= 2 ? 'text-amber-600' : 'text-slate-400'}`}>
                      %{agent.conversion_rate}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Agent Performance Chart */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Danısman Bazli Donusum</h3>
          {agentData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={agentData.slice(0, 10)} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 11 }} />
                <Bar dataKey="kayit_ok" name="Kayit OK" fill="#10b981" radius={[0, 4, 4, 0]} />
                <Bar dataKey="potansiyel" name="Potansiyel" fill="#6366f1" radius={[0, 4, 4, 0]} />
                <Bar dataKey="dusunecek" name="Dusunecek" fill="#f59e0b" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">Veri yok</p>}
        </div>

        {/* Course Distribution */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Ders Bazli Dagilim</h3>
          {courseData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={courseData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="course" tick={{ fontSize: 9 }} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 11 }} />
                <Bar dataKey="count" name="Adet" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">Veri yok</p>}
        </div>

        {/* Ad Type Distribution */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Reklam Turu Dagilimi</h3>
          {adData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <RePieChart>
                <Pie data={adData} dataKey="count" nameKey="ad_type" cx="50%" cy="50%" outerRadius={90} innerRadius={45} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}>
                  {adData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 11 }} />
              </RePieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">Veri yok</p>}
        </div>

        {/* Daily Trend */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Gunluk Islem Trendi</h3>
          {dailyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={dailyTrend}>
                <defs>
                  <linearGradient id="reportAreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
                <Area type="monotone" dataKey="actions" name="Islem" stroke="#6366f1" fill="url(#reportAreaGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">Veri yok</p>}
        </div>
      </div>
    </div>
  );
}

// ==================== AGENT VIEW TAB ====================
function CRMAgentView({ reports, token, agents, onSelectLead, statusColors }) {
  const [selectedAgent, setSelectedAgent] = useState('');
  const [agentLeads, setAgentLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const agentPerf = reports?.agent_performance || [];
  const currentAgent = agentPerf.find(a => a.name === selectedAgent);

  const fetchAgentLeads = useCallback(async () => {
    if (!selectedAgent) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/crm/leads`, { headers, params: { agent_name: selectedAgent, per_page: 20 } });
      setAgentLeads(res.data.leads);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedAgent, token]);

  useEffect(() => { fetchAgentLeads(); }, [fetchAgentLeads]);

  return (
    <div className="space-y-5" data-testid="crm-agent-view">
      {/* Agent Selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4 flex-wrap">
        <User size={18} className="text-blue-500" />
        <span className="text-sm font-medium text-slate-600">Danısman Secin:</span>
        <Select value={selectedAgent} onValueChange={setSelectedAgent}>
          <SelectTrigger className="w-[250px] bg-white border-slate-200" data-testid="agent-select">
            <SelectValue placeholder="Danısman seciniz..." />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200 text-slate-800">
            {agents.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!selectedAgent ? (
        /* Agent Grid Cards */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agentPerf.map((agent, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md hover:border-blue-200 cursor-pointer transition-all group"
              onClick={() => setSelectedAgent(agent.name)}
              data-testid={`agent-card-${i}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                  {agent.name.charAt(0)}
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-800 group-hover:text-blue-700 transition">{agent.name}</div>
                  <div className="text-xs text-slate-400">{agent.total} data</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 bg-emerald-50 rounded-lg">
                  <div className="text-base font-bold text-emerald-600">{agent.kayit_ok}</div>
                  <div className="text-[10px] text-emerald-500">Kayit OK</div>
                </div>
                <div className="text-center p-2 bg-indigo-50 rounded-lg">
                  <div className="text-base font-bold text-indigo-600">{agent.potansiyel}</div>
                  <div className="text-[10px] text-indigo-500">Potansiyel</div>
                </div>
                <div className="text-center p-2 bg-amber-50 rounded-lg">
                  <div className="text-base font-bold text-amber-600">%{agent.conversion_rate}</div>
                  <div className="text-[10px] text-amber-500">Donusum</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Selected Agent Detail */
        <div className="space-y-5">
          {/* Agent Stats */}
          {currentAgent && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <StatMini label="Toplam" value={currentAgent.total} color="bg-slate-100 text-slate-700" />
              <StatMini label="Kayit OK" value={currentAgent.kayit_ok} color="bg-emerald-100 text-emerald-700" />
              <StatMini label="Potansiyel" value={currentAgent.potansiyel} color="bg-indigo-100 text-indigo-700" />
              <StatMini label="Aranmayan" value={currentAgent.aranmayan} color="bg-cyan-100 text-cyan-700" />
              <StatMini label="Cevap Yok" value={currentAgent.cevap_yok} color="bg-blue-100 text-blue-700" />
              <StatMini label="Ort. Arama" value={currentAgent.avg_calls} color="bg-violet-100 text-violet-700" />
              <StatMini label="Donusum" value={`%${currentAgent.conversion_rate}`} color="bg-amber-100 text-amber-700" />
            </div>
          )}

          {/* Agent's Recent Leads */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="p-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <FileText size={14} className="text-blue-500" />
                {selectedAgent} - Son Datalar
                <button onClick={() => setSelectedAgent('')} className="ml-auto text-xs text-blue-500 hover:underline">Tum Danismanlara Don</button>
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/80">
                  <tr className="border-b border-slate-200">
                    <th className="p-3 text-left text-xs font-semibold text-slate-500">Ad Soyad</th>
                    <th className="p-3 text-left text-xs font-semibold text-slate-500">Telefon</th>
                    <th className="p-3 text-left text-xs font-semibold text-slate-500">Durum</th>
                    <th className="p-3 text-center text-xs font-semibold text-slate-500">Arama</th>
                    <th className="p-3 text-left text-xs font-semibold text-slate-500">Ders</th>
                    <th className="p-3 text-left text-xs font-semibold text-slate-500">Tarih</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={6} className="p-8 text-center text-slate-400">Yukleniyor...</td></tr>
                  ) : agentLeads.length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-slate-400">Data bulunamadi</td></tr>
                  ) : agentLeads.map((lead, idx) => (
                    <tr key={lead.id} className="hover:bg-blue-50/40 cursor-pointer transition" onClick={() => onSelectLead(lead)}>
                      <td className="p-3 font-medium text-slate-800">{lead.customer_name}</td>
                      <td className="p-3 text-xs text-slate-500 font-mono">{lead.phone}</td>
                      <td className="p-3"><span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${statusColors[lead.status] || 'bg-slate-200 text-slate-600'}`}>{lead.status}</span></td>
                      <td className="p-3 text-center text-xs">{lead.call_count || 0}</td>
                      <td className="p-3 text-xs text-slate-500">{lead.course_name || '-'}</td>
                      <td className="p-3 text-xs text-slate-400">{lead.data_date || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatMini({ label, value, color }) {
  return (
    <div className={`rounded-lg p-3 text-center ${color}`}>
      <div className="text-lg font-bold">{value}</div>
      <div className="text-[10px] font-medium opacity-70">{label}</div>
    </div>
  );
}

// ==================== LEAD DETAIL (SHARED) ====================
function LeadDetail({ lead: initialLead, token, agents, onBack, statusColors, statusList }) {
  const [lead, setLead] = useState(initialLead);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [noteStatus, setNoteStatus] = useState('');
  const [noteText, setNoteText] = useState('');
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('08:00');
  const [reminderNote, setReminderNote] = useState('');
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/crm/leads/${lead.id}`, editData, { headers });
      setLead(res.data);
      setEditing(false);
    } catch (err) { alert('Kayit hatasi'); }
    finally { setSaving(false); }
  };

  const handleAddNote = async () => {
    if (!noteStatus) return alert('Durum seciniz');
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/crm/leads/${lead.id}/notes`, {
        status: noteStatus, note: noteText, agent_name: lead.agent_name
      }, { headers });
      setLead(res.data);
      setNoteStatus('');
      setNoteText('');
    } catch (err) { alert('Not ekleme hatasi'); }
    finally { setSaving(false); }
  };

  const handleSetReminder = async () => {
    if (!reminderDate) return alert('Tarih seciniz');
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/crm/leads/${lead.id}/reminder`, {
        reminder_date: reminderDate, reminder_time: reminderTime, note: reminderNote
      }, { headers });
      setLead(res.data);
      setReminderDate('');
      setReminderNote('');
    } catch (err) { alert('Hatirlatma hatasi'); }
    finally { setSaving(false); }
  };

  const callHistory = (lead.call_history || []).slice().reverse();

  return (
    <div className="space-y-4" data-testid="lead-detail">
      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg transition" data-testid="back-btn">
              <ArrowLeft size={18} className="text-slate-500" />
            </button>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
              {lead.customer_name?.charAt(0) || '?'}
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">{lead.customer_name}</h2>
              <div className="flex items-center gap-3 text-sm text-slate-500">
                <a href={`tel:${lead.phone}`} className="text-blue-600 hover:underline flex items-center gap-1">
                  <Phone size={13} /> {lead.phone}
                </a>
                <a href={`https://wa.me/90${lead.phone?.replace(/^0/, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline flex items-center gap-1">
                  <MessageCircle size={13} /> WhatsApp
                </a>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusColors[lead.status] || 'bg-slate-200 text-slate-600'}`}>
              {lead.status}
            </span>
            <span className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
              {lead.call_count || 0} Arama
            </span>
            {!editing ? (
              <Button size="sm" variant="outline" onClick={() => { setEditing(true); setEditData({
                customer_name: lead.customer_name, phone: lead.phone, email: lead.email,
                city: lead.city, education_level: lead.education_level, gender: lead.gender,
                profession: lead.profession, course_name: lead.course_name, ad_type: lead.ad_type,
                agent_name: lead.agent_name, priority: lead.priority
              }); }} data-testid="edit-btn">
                <Edit2 size={13} className="mr-1" /> Duzenle
              </Button>
            ) : (
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-600 text-white" data-testid="save-btn">
                <Save size={13} className="mr-1" /> Kaydet
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-4">
          {/* Info Card */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Bilgiler</h3>
            {!editing ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <InfoField label="Ders/Basvuru" value={lead.course_name} />
                <InfoField label="Reklam Turu" value={lead.ad_type} />
                <InfoField label="Email" value={lead.email} />
                <InfoField label="Sehir" value={lead.city} />
                <InfoField label="Egitim" value={lead.education_level} />
                <InfoField label="Cinsiyet" value={lead.gender} />
                <InfoField label="Meslek" value={lead.profession} />
                <InfoField label="Danısman" value={lead.agent_name} />
                <InfoField label="Kaynak" value={lead.source} />
                <InfoField label="Data Tarihi" value={lead.data_date} />
                <InfoField label="Saat" value={lead.record_datetime && lead.record_datetime.length > 10 ? lead.record_datetime.slice(11, 16) : '-'} />
                <InfoField label="Not" value={lead.note} className="col-span-2 md:col-span-3" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <EditField label="Ad Soyad" value={editData.customer_name} onChange={v => setEditData(d => ({...d, customer_name: v}))} />
                <EditField label="Telefon" value={editData.phone} onChange={v => setEditData(d => ({...d, phone: v}))} />
                <EditField label="Email" value={editData.email} onChange={v => setEditData(d => ({...d, email: v}))} />
                <EditField label="Sehir" value={editData.city} onChange={v => setEditData(d => ({...d, city: v}))} />
                <EditField label="Egitim" value={editData.education_level} onChange={v => setEditData(d => ({...d, education_level: v}))} />
                <EditField label="Ders" value={editData.course_name} onChange={v => setEditData(d => ({...d, course_name: v}))} />
                <EditField label="Reklam Turu" value={editData.ad_type} onChange={v => setEditData(d => ({...d, ad_type: v}))} />
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Danısman</label>
                  <Select value={editData.agent_name || ''} onValueChange={v => setEditData(d => ({...d, agent_name: v}))}>
                    <SelectTrigger className="bg-white border-slate-200 h-9"><SelectValue placeholder="Sec" /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 text-slate-800">
                      {agents.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          {/* Add Note */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Arama Kaydi Ekle</h3>
            <div className="space-y-3">
              <Select value={noteStatus} onValueChange={setNoteStatus}>
                <SelectTrigger className="w-full sm:w-[250px] bg-white border-slate-200" data-testid="note-status-select">
                  <SelectValue placeholder="Durum Sec" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-800 max-h-[300px]">
                  {statusList.map(s => (
                    <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <textarea
                className="w-full p-3 border border-slate-200 rounded-lg text-sm bg-white resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                rows={2}
                placeholder="Aciklama / Not ekleyin..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                data-testid="note-text"
              />
              <Button onClick={handleAddNote} disabled={saving || !noteStatus} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-note-btn">
                <CheckCircle size={14} className="mr-1" /> Kaydet
              </Button>
            </div>
          </div>

          {/* Reminder */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Bell size={14} /> Hatirlatma Ayarla
            </h3>
            <div className="flex gap-2 flex-wrap">
              <Input type="date" className="w-[160px] bg-white border-slate-200 h-9" value={reminderDate} onChange={e => setReminderDate(e.target.value)} data-testid="reminder-date" />
              <Input type="time" className="w-[110px] bg-white border-slate-200 h-9" value={reminderTime} onChange={e => setReminderTime(e.target.value)} />
              <Input className="flex-1 bg-white border-slate-200 h-9" placeholder="Hatirlatma notu..." value={reminderNote} onChange={e => setReminderNote(e.target.value)} />
              <Button onClick={handleSetReminder} disabled={saving || !reminderDate} className="bg-amber-500 hover:bg-amber-600 text-white h-9" data-testid="set-reminder-btn">
                <Clock size={14} className="mr-1" /> Hatırlat
              </Button>
            </div>
            {lead.reminder_date && !lead.reminder_completed && (
              <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-sm mt-3">
                <Bell size={14} className="text-amber-600" />
                <span className="text-amber-700">Aktif: {lead.reminder_date} {lead.reminder_time || ''}</span>
              </div>
            )}
          </div>
        </div>

        {/* Right: Call History */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Arama Gecmisi ({callHistory.length})</h3>
            {callHistory.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">Henuz arama kaydi yok</p>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                {callHistory.map((entry, idx) => (
                  <div key={entry.id || idx} className="border border-slate-100 rounded-lg p-3 bg-slate-50/50 hover:bg-white transition" data-testid={`history-${idx}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-700 text-white text-[10px] font-bold">
                        {callHistory.length - idx}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${statusColors[entry.status] || 'bg-slate-200 text-slate-600'}`}>
                        {entry.status}
                      </span>
                    </div>
                    <div className="flex gap-2 flex-wrap mt-1">
                      <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{entry.agent_name}</span>
                      <span className="text-[10px] text-slate-400">
                        {entry.created_at ? new Date(entry.created_at).toLocaleString('tr-TR') : ''}
                      </span>
                    </div>
                    {entry.note && <p className="mt-1.5 text-xs text-slate-600">{entry.note}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {lead.reminders && lead.reminders.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Hatirlatmalar</h3>
              <div className="space-y-2">
                {lead.reminders.map((r, i) => (
                  <div key={r.id || i} className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-100 rounded-lg text-sm">
                    <Calendar size={13} className="text-amber-500" />
                    <span className="text-amber-700 font-medium text-xs">{r.date} {r.time}</span>
                    {r.note && <span className="text-xs text-slate-500">- {r.note}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== HELPERS ====================
function InfoField({ label, value, className = '' }) {
  return (
    <div className={className}>
      <span className="text-[10px] text-slate-400 block">{label}</span>
      <span className="text-slate-700 text-sm">{value || '-'}</span>
    </div>
  );
}

function EditField({ label, value, onChange }) {
  return (
    <div>
      <label className="text-xs text-slate-500 mb-1 block">{label}</label>
      <Input className="bg-white border-slate-200 h-9" value={value || ''} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

// ==================== IMPORT MODAL ====================
function ImportModal({ token, onClose, onImported }) {
  const [data, setData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState([]);
  const fileRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) return alert('Dosya bos veya gecersiz');
      const headerLine = lines[0].split(/[,;\t]/);
      const rows = lines.slice(1).map(line => {
        const cols = line.split(/[,;\t]/);
        const obj = {};
        headerLine.forEach((h, i) => { obj[h.trim()] = (cols[i] || '').trim(); });
        return obj;
      });
      setData(rows);
      setPreview(rows.slice(0, 5));
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleImport = async () => {
    if (data.length === 0) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/api/crm/import-excel`, { data }, { headers });
      alert(`${res.data.imported} kayit basariyla aktarildi`);
      onImported();
    } catch (err) {
      alert('Import hatasi: ' + (err.response?.data?.detail || err.message));
    } finally { setImporting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="import-section">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Upload size={20} className="text-emerald-600" /> Excel/CSV Yukle
            </h2>
            <p className="text-sm text-slate-500 mt-1">Toplu data aktarimi icin dosyanizi yukleyin</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg"><X size={18} className="text-slate-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-blue-300 transition cursor-pointer" onClick={() => fileRef.current?.click()}>
            <Upload size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500 mb-1">CSV veya Excel dosyanizi secin</p>
            <p className="text-xs text-slate-400">Sutunlar: Ad Soyad, Telefon, Email, Sehir, Egitim, Ders, Reklam Turu, Danısman</p>
            <input ref={fileRef} type="file" accept=".csv,.txt,.xls,.xlsx" onChange={handleFile} className="hidden" />
          </div>

          {preview.length > 0 && (
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Onizleme ({data.length} kayit)</p>
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>{Object.keys(preview[0]).map(k => <th key={k} className="p-2 text-left text-slate-500">{k}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {preview.map((r, i) => (
                      <tr key={i}>{Object.values(r).map((v, j) => <td key={j} className="p-2 text-slate-600">{v}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button onClick={handleImport} disabled={importing || data.length === 0} className="bg-blue-600 text-white flex-1 h-11" data-testid="import-submit">
              {importing ? 'Aktariliyor...' : `${data.length} Kaydi Aktar`}
            </Button>
            <Button variant="outline" onClick={onClose} className="h-11">Iptal</Button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ==================== STATUS SETTINGS ====================
const COLOR_OPTIONS = [
  { value: "bg-cyan-500", label: "Cyan" },
  { value: "bg-blue-600", label: "Mavi" },
  { value: "bg-green-500", label: "Yesil" },
  { value: "bg-emerald-500", label: "Zumrut" },
  { value: "bg-teal-500", label: "Teal" },
  { value: "bg-indigo-500", label: "Indigo" },
  { value: "bg-purple-500", label: "Mor" },
  { value: "bg-pink-500", label: "Pembe" },
  { value: "bg-rose-600", label: "Gul" },
  { value: "bg-red-500", label: "Kirmizi" },
  { value: "bg-orange-500", label: "Turuncu" },
  { value: "bg-amber-600", label: "Amber" },
  { value: "bg-yellow-600", label: "Sari" },
  { value: "bg-gray-500", label: "Gri" },
  { value: "bg-slate-600", label: "Koyu Gri" },
  { value: "bg-sky-500", label: "Gok Mavi" },
  { value: "bg-violet-500", label: "Eflatun" },
];

function StatusSettings({ token, statusList, onRefresh }) {
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('bg-slate-500');
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/crm/statuses`, { name: newName.trim(), color: newColor }, { headers });
      setNewName('');
      setNewColor('bg-slate-500');
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Ekleme hatasi');
    } finally { setSaving(false); }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`"${name}" durumunu silmek istediginize emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/api/crm/statuses/${encodeURIComponent(name)}`, { headers });
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Silme hatasi');
    }
  };

  return (
    <div className="space-y-6" data-testid="status-settings">
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <Settings size={20} className="text-blue-600" />
            Durum Yonetimi
          </CardTitle>
          <p className="text-sm text-slate-500">Data durumlarini ekleyin veya silin. Tum CRM'de kullanilir.</p>
        </CardHeader>
        <CardContent>
          {/* Add New Status */}
          <div className="flex items-end gap-3 mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="add-status-form">
            <div className="flex-1">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Durum Adi</label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Orn: SIKINTILI, BEKLEMEDE..."
                className="bg-white border-slate-300 text-slate-800 h-10"
                data-testid="new-status-name"
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              />
            </div>
            <div className="w-48">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Renk</label>
              <Select value={newColor} onValueChange={setNewColor}>
                <SelectTrigger className="bg-white border-slate-300 h-10 text-slate-800" data-testid="new-status-color">
                  <div className="flex items-center gap-2">
                    <span className={`w-4 h-4 rounded ${newColor}`}></span>
                    <SelectValue />
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-800">
                  {COLOR_OPTIONS.map(c => (
                    <SelectItem key={c.value} value={c.value}>
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded ${c.value}`}></span>
                        {c.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAdd} disabled={saving || !newName.trim()} className="bg-blue-600 hover:bg-blue-700 text-white h-10" data-testid="add-status-btn">
              <Plus size={16} className="mr-1" /> Ekle
            </Button>
          </div>

          {/* Status List */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="status-list">
            {statusList.map((status) => (
              <div
                key={status.name}
                className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition"
                data-testid={`status-item-${status.name}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${status.color}`}></span>
                  <span className="text-sm font-medium text-slate-700">{status.name}</span>
                </div>
                <button
                  onClick={() => handleDelete(status.name)}
                  className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition"
                  title="Sil"
                  data-testid={`delete-status-${status.name}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
          {statusList.length === 0 && (
            <p className="text-center text-slate-400 py-8">Henuz durum eklenmemis</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
